import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';

/**
 * L'adresse qui ouvre l'application, et ou elle mene.
 *
 * Un post partage hors de Sirocco mene a une page web qui propose
 * d'ouvrir l'application — `sirocco://post/<id>`. Sans traduction, ce
 * schema ouvrait Sirocco sur son ecran d'accueil : on arrivait dans
 * l'application sans le post qu'on venait chercher.
 *
 * Meme construction que les notifications touchees : l'adresse peut
 * **lancer** l'application, donc arriver bien avant que le routeur
 * existe. Le natif la garde, on vient la reclamer.
 */
const Lien = registerPlugin<{
  enAttente(): Promise<{ lien: string }>;
  addListener(
    evenement: 'lien',
    ecouteur: (donnees: { lien: string }) => void,
  ): Promise<PluginListenerHandle>;
}>('Lien');

function natif(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Ou mene une adresse.
 *
 * Deux formes : un post, un evenement. Les autres viendront ici, comme la
 * table des notifications.
 */
export function destinationDuLien(lien: string): string | null {
  const post = /^sirocco:\/\/post\/([A-Za-z0-9_-]+)/.exec(lien);
  if (post) return `/app/feed/${post[1]}/comments`;
  const evenement = /^sirocco:\/\/event\/([A-Za-z0-9_-]+)/.exec(lien);
  if (evenement) return `/app/events/${evenement[1]}`;
  return null;
}

/**
 * Branche le routeur sur les adresses recues.
 *
 * Reclame d'abord celle qui attendait — l'application a peut-etre ete
 * lancee par elle — puis ecoute les suivantes, pour le cas ou elle
 * tournait deja.
 */
export function ecouterLiens(aller: (route: string) => void): () => void {
  if (!natif()) return () => undefined;

  const attente = Lien.addListener('lien', ({ lien }) => {
    const route = destinationDuLien(lien);
    if (route) aller(route);
  });

  void Lien.enAttente()
    .then(({ lien }) => {
      const route = lien ? destinationDuLien(lien) : null;
      if (route) aller(route);
    })
    .catch(() => undefined);

  return () => void attente.then((h) => h.remove()).catch(() => undefined);
}
